import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Target as TargetIcon } from "lucide-react";
import { fmtEur } from "@/lib/format";
import { toast } from "sonner";

const empty = { title: "", metric: "ricavi", target: 1000, period: "mensile" };

const metricLabel = { ricavi: "Ricavi", ore: "Ore", clienti: "Clienti", progetti: "Progetti" };

export default function Goals() {
  const [goals, setGoals] = useState([]);
  const [report, setReport] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);

  const load = async () => {
    const [g, r] = await Promise.all([api.get("/goals"), api.get("/reports/dashboard")]);
    setGoals(g.data); setReport(r.data);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.title) return toast.error("Inserisci un titolo");
    try {
      await api.post("/goals", form);
      toast.success("Obiettivo creato"); setOpen(false); setForm(empty); load();
    } catch { toast.error("Errore"); }
  };

  const remove = async (id) => {
    await api.delete(`/goals/${id}`);
    load();
  };

  const currentFor = (metric) => {
    if (!report) return 0;
    switch (metric) {
      case "ricavi": return report.kpi.total_revenue;
      case "ore": return report.kpi.billable_hours;
      case "clienti": return report.kpi.clients_count;
      case "progetti": return report.kpi.active_projects;
      default: return 0;
    }
  };

  return (
    <div className="space-y-6" data-testid="goals-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <span className="zen-label">Traguardi</span>
          <h1 className="mt-2 font-heading text-3xl">Obiettivi</h1>
          <p className="text-zen-ink2 mt-1">Traccia i tuoi traguardi mensili, trimestrali e annuali.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-goal-btn" className="bg-zen-primary hover:bg-zen-primaryHover rounded-full"><Plus size={16} className="mr-1"/>Nuovo obiettivo</Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Nuovo obiettivo</DialogTitle>
              <DialogDescription className="sr-only">Definisci titolo, metrica, periodo e target dell&apos;obiettivo.</DialogDescription>
            </DialogHeader>
            <div className="grid gap-4">
              <div><Label>Titolo</Label><Input data-testid="gf-title" value={form.title} onChange={(e)=>setForm({...form, title:e.target.value})} placeholder="Es. Ricavi trimestrali"/></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Metrica</Label>
                  <Select value={form.metric} onValueChange={(v)=>setForm({...form, metric:v})}>
                    <SelectTrigger><SelectValue/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ricavi">Ricavi (€)</SelectItem>
                      <SelectItem value="ore">Ore fatturabili</SelectItem>
                      <SelectItem value="clienti">Numero clienti</SelectItem>
                      <SelectItem value="progetti">Progetti attivi</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Periodo</Label>
                  <Select value={form.period} onValueChange={(v)=>setForm({...form, period:v})}>
                    <SelectTrigger><SelectValue/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="mensile">Mensile</SelectItem>
                      <SelectItem value="trimestrale">Trimestrale</SelectItem>
                      <SelectItem value="annuale">Annuale</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label>Target</Label><Input data-testid="gf-target" type="number" step="1" value={form.target} onChange={(e)=>setForm({...form, target:parseFloat(e.target.value)||0})}/></div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={()=>setOpen(false)}>Annulla</Button>
              <Button data-testid="gf-save" onClick={save} className="bg-zen-primary hover:bg-zen-primaryHover">Crea</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {goals.length === 0 ? (
        <div className="zen-card p-12 text-center text-zen-ink2">Nessun obiettivo. Definiscine uno per iniziare!</div>
      ) : (
        <div className="grid md:grid-cols-2 gap-5">
          {goals.map(g => {
            const cur = currentFor(g.metric);
            const pct = Math.min(100, Math.round((cur / g.target) * 100));
            const fmt = g.metric === "ricavi" ? fmtEur : (v) => v.toFixed(g.metric==="ore"?1:0);
            return (
              <div key={g.goal_id} data-testid={`goal-${g.goal_id}`} className="zen-card p-6 group">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-zen-primary/10 text-zen-primary flex items-center justify-center"><TargetIcon size={18} strokeWidth={1.5}/></div>
                    <div>
                      <h3 className="font-heading text-lg">{g.title}</h3>
                      <div className="text-xs text-zen-ink2 uppercase tracking-wider">{metricLabel[g.metric]} · {g.period}</div>
                    </div>
                  </div>
                  <button onClick={()=>remove(g.goal_id)} className="text-zen-alert opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 size={15}/></button>
                </div>
                <div className="flex items-baseline justify-between mb-2">
                  <div className="font-heading text-2xl">{fmt(cur)}</div>
                  <div className="text-sm text-zen-ink2">di {fmt(g.target)}</div>
                </div>
                <Progress value={pct} className="h-2 mb-2"/>
                <div className="flex justify-between text-xs text-zen-ink2">
                  <span>{pct}% raggiunto</span>
                  {pct >= 100 && <Badge className="bg-zen-primary text-white rounded-full font-normal">✓ Completato</Badge>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
